import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// Styled Components
const PageWrapper = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #4338CA 0%, #7C3AED 100%);
  color: white;
  padding: 100px 0 80px;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto 2.5rem;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 3rem;
`;

const SwitchLabel = styled.span`
  font-size: 1rem;
  font-weight: 500;
  margin: 0 1rem;
  opacity: ${props => props.active ? '1' : '0.7'};
`;

const Switch = styled.div`
  position: relative;
  width: 60px;
  height: 32px;
  border-radius: 16px;
  background-color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.4);
  }
`;

const SwitchHandle = styled.div`
  position: absolute;
  top: 4px;
  left: ${props => props.position === 'yearly' ? '32px' : '4px'};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: white;
  transition: left 0.3s;
`;

const PricingSection = styled.section`
  padding: 80px 0;
  background-color: var(--bg-white);
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.div`
  background-color: var(--bg-white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
  display: flex;
  flex-direction: column;
  height: 100%;
  
  ${props => props.featured && `
    transform: scale(1.05);
    box-shadow: var(--shadow-xl);
    
    @media (max-width: 992px) {
      transform: scale(1);
    }
  `}
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-lg);
  }
`;

const PricingHeader = styled.div`
  padding: 2rem;
  background-color: ${props => props.featured ? 'var(--primary)' : 'var(--bg-white)'};
  color: ${props => props.featured ? 'white' : 'var(--text-dark)'};
  border-bottom: 1px solid ${props => props.featured ? 'rgba(255, 255, 255, 0.1)' : 'var(--bg-light)'};
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PlanDescription = styled.p`
  font-size: 0.9rem;
  opacity: 0.8;
  margin-bottom: 1.5rem;
`;

const PlanPrice = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  
  span {
    font-size: 1rem;
    font-weight: normal;
    opacity: 0.7;
  }
`;

const PlanPeriod = styled.p`
  font-size: 0.9rem;
  opacity: 0.7;
  margin-top: 0.5rem;
`;

const PricingBody = styled.div`
  padding: 2rem;
  flex: 1;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckIcon = styled.div`
  margin-right: 0.75rem;
  color: ${props => props.emphasized ? 'var(--primary)' : 'var(--success)'};
  flex-shrink: 0;
  margin-top: 0.2rem;
`;

const PricingFooter = styled.div`
  padding: 0 2rem 2rem;
`;

const CTAButton = styled(Link)`
  display: block;
  width: 100%;
  padding: 0.75rem 1.5rem;
  text-align: center;
  font-weight: 600;
  border-radius: var(--radius-md);
  transition: all var(--transition-normal);
  
  ${props => props.primary ? `
    background-color: var(--primary);
    color: white;
    
    &:hover {
      background-color: var(--primary-dark);
      color: white;
    }
  ` : `
    background-color: var(--bg-light);
    color: var(--text-dark);
    
    &:hover {
      background-color: var(--bg-gray);
      color: var(--text-dark);
    }
  `}
`;

const FAQSection = styled.section`
  background-color: var(--bg-light);
  padding: 80px 0;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background-color: var(--bg-white);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: var(--shadow-sm);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border: none;
  background-color: transparent;
  cursor: pointer;
  text-align: left;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-dark);
  transition: all var(--transition-fast);
  
  &:hover {
    color: var(--primary);
  }
  
  &:focus {
    outline: none;
  }
`;

const FAQAnswer = styled.div`
  padding: 0 1.5rem 1.5rem;
  color: var(--text-light);
  line-height: 1.6;
`;

const ChevronIcon = ({ isOpen }) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.3s ease'
        }}
    >
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
);

// Main Component
const PricingPage = () => {
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [openFaqItems, setOpenFaqItems] = useState(new Set([1]));

    const toggleBillingCycle = () => {
        setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly');
    };

    const toggleFaqItem = (id) => {
        const newOpenItems = new Set(openFaqItems);
        if (newOpenItems.has(id)) {
            newOpenItems.delete(id);
        } else {
            newOpenItems.add(id);
        }
        setOpenFaqItems(newOpenItems);
    };

    // Pricing plans data
    const plans = [
        {
            id: 'basic',
            name: '入門方案',
            description: '適合小型企業或初創公司',
            monthlyPrice: 2999,
            yearlyPrice: 29988,
            yearlyDiscount: 4000,
            features: [
                '基本電商功能',
                '固定模板 (3選1)',
                '支援100個商品',
                '每月訂單數上限500筆',
                '支援2種付款方式',
                '基本數據報表',
                'Email支援'
            ],
            featured: false
        },
        {
            id: 'standard',
            name: '標準方案',
            description: '適合中小型企業',
            monthlyPrice: 4999,
            yearlyPrice: 49990,
            yearlyDiscount: 10000,
            features: [
                '全部電商功能',
                '進階版模板 (5選1)',
                '支援無限商品',
                '無訂單數上限',
                '支援所有付款方式',
                '進階數據報表',
                '優先Email支援',
                '電話技術支援',
                '行銷自動化工具'
            ],
            featured: true
        },
        {
            id: 'enterprise',
            name: '企業方案',
            description: '適合大型企業與品牌',
            monthlyPrice: 9999,
            yearlyPrice: 99990,
            yearlyDiscount: 20000,
            features: [
                '全部電商功能',
                '完全客製化設計',
                '支援無限商品',
                '無訂單數上限',
                '支援所有付款方式',
                '高級數據分析工具',
                '24/7 專屬客服',
                'API存取權限',
                '多站點支援',
                '專屬客戶經理'
            ],
            featured: false
        }
    ];

    // FAQ data
    const faqItems = [
        {
            id: 1,
            question: '有試用期嗎？',
            answer: '是的，我們提供14天的免費試用期，您可以在此期間測試所有功能，無需信用卡。試用期結束後，您可以選擇升級到付費方案或繼續使用有限功能的免費版本。'
        },
        {
            id: 2,
            question: '可以從入門版升級到標準版嗎？',
            answer: '可以，您可以隨時升級您的方案。升級時，我們會按比例計算剩餘訂閱期的費用差額。所有您的數據和設置都會保留。'
        },
        {
            id: 3,
            question: '可以退款嗎？',
            answer: '我們提供30天的退款保證。如果您在購買後30天內對我們的服務不滿意，可以申請全額退款。年度訂閱則按使用時間比例計算退款金額。'
        },
        {
            id: 4,
            question: '是否提供發票？',
            answer: '是的，我們提供正式電子發票和紙本發票。電子發票會在付款後自動發送到您的郵箱，如需紙本發票，請聯繫我們的客服團隊。'
        }
    ];

    return (
        <PageWrapper>
            <HeroSection>
                <Container>
                    <HeroTitle>簡單透明的定價方案</HeroTitle>
                    <HeroSubtitle>
                        選擇最適合您業務需求的方案，無隱藏費用。所有方案皆包含核心功能，可隨業務成長調整。
                    </HeroSubtitle>

                    <SwitchContainer>
                        <SwitchLabel active={billingCycle === 'monthly'}>月付</SwitchLabel>
                        <Switch onClick={toggleBillingCycle}>
                            <SwitchHandle position={billingCycle} />
                        </Switch>
                        <SwitchLabel active={billingCycle === 'yearly'}>年付</SwitchLabel>
                    </SwitchContainer>
                </Container>
            </HeroSection>

            <PricingSection>
                <Container>
                    <PricingGrid>
                        {plans.map(plan => (
                            <PricingCard key={plan.id} featured={plan.featured}>
                                <PricingHeader featured={plan.featured}>
                                    <PlanName>{plan.name}</PlanName>
                                    <PlanDescription>{plan.description}</PlanDescription>
                                    <PlanPrice>
                                        {billingCycle === 'monthly'
                                            ? `NT$${plan.monthlyPrice.toLocaleString()}`
                                            : `NT$${plan.yearlyPrice.toLocaleString()}`
                                        }
                                        <span>/{billingCycle === 'monthly' ? '月' : '年'}</span>
                                    </PlanPrice>
                                    {billingCycle === 'yearly' && (
                                        <PlanPeriod>年付省 NT${plan.yearlyDiscount.toLocaleString()}</PlanPeriod>
                                    )}
                                </PricingHeader>

                                <PricingBody>
                                    <FeaturesList>
                                        {plan.features.map((feature, index) => (
                                            <FeatureItem key={index}>
                                                <CheckIcon emphasized={plan.featured}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </CheckIcon>
                                                {feature}
                                            </FeatureItem>
                                        ))}
                                    </FeaturesList>
                                </PricingBody>

                                <PricingFooter>
                                    <CTAButton
                                        to="/contact"
                                        primary={plan.featured}
                                    >
                                        {plan.featured ? '立即開始' : '選擇方案'}
                                    </CTAButton>
                                </PricingFooter>
                            </PricingCard>
                        ))}
                    </PricingGrid>
                </Container>
            </PricingSection>

            <FAQSection>
                <Container>
                    <SectionHeader>
                        <SectionTitle>關於方案的常見問題</SectionTitle>
                        <SectionDescription>
                            還有更多問題？歡迎隨時聯繫我們的客服團隊
                        </SectionDescription>
                    </SectionHeader>

                    <FAQContainer>
                        {faqItems.map(item => (
                            <FAQItem key={item.id}>
                                <FAQQuestion
                                    onClick={() => toggleFaqItem(item.id)}
                                    aria-expanded={openFaqItems.has(item.id)}
                                >
                                    {item.question}
                                    <ChevronIcon isOpen={openFaqItems.has(item.id)} />
                                </FAQQuestion>

                                {openFaqItems.has(item.id) && (
                                    <FAQAnswer>{item.answer}</FAQAnswer>
                                )}
                            </FAQItem>
                        ))}
                    </FAQContainer>
                </Container>
            </FAQSection>
        </PageWrapper>
    );
};

export default PricingPage;